'use client';

import { motion } from 'framer-motion';

function Ring({
  size,
  duration,
  delay,
  opacity,
  reverse,
}: {
  size: number;
  duration: number;
  delay: number;
  opacity: number;
  reverse?: boolean;
}) {
  return (
    <motion.div
      className="absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: `hsl(162 63% 46% / ${opacity})`,
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
        rotate: reverse ? -360 : 360,
      }}
      transition={{
        opacity: { duration: 0.8, delay },
        scale: { duration: 0.8, delay },
        rotate: { duration, repeat: Infinity, ease: 'linear' },
      }}
    />
  );
}

function Orb({
  size,
  orbitSize,
  duration,
  delay,
  color,
  startAngle,
}: {
  size: number;
  orbitSize: number;
  duration: number;
  delay: number;
  color: string;
  startAngle: number;
}) {
  return (
    <motion.div
      className="absolute left-1/2 top-1/2"
      style={{
        width: orbitSize,
        height: orbitSize,
        marginLeft: -orbitSize / 2,
        marginTop: -orbitSize / 2,
      }}
      initial={{ opacity: 0, rotate: startAngle }}
      animate={{ opacity: 1, rotate: startAngle + 360 }}
      transition={{
        opacity: { duration: 0.6, delay },
        rotate: { duration, repeat: Infinity, ease: 'linear' },
      }}
    >
      <motion.div
        className="absolute rounded-full"
        style={{
          width: size,
          height: size,
          top: -size / 2,
          left: '50%',
          marginLeft: -size / 2,
          background: color,
          boxShadow: `0 0 ${size * 2}px ${color}, 0 0 ${size * 4}px ${color}`,
        }}
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.div>
  );
}

function FloatingDot({
  x,
  y,
  size,
  delay,
  duration,
}: {
  x: string;
  y: string;
  size: number;
  delay: number;
  duration: number;
}) {
  return (
    <motion.div
      className="absolute rounded-full bg-primary"
      style={{
        width: size,
        height: size,
        left: x,
        top: y,
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0, 0.6, 0],
        y: [0, -20, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

function PulseRing({
  size,
  delay,
  duration,
}: {
  size: number;
  delay: number;
  duration: number;
}) {
  return (
    <motion.div
      className="absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        border: '1px solid hsl(162 63% 46% / 0.15)',
      }}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: [0, 0.4, 0], scale: [0.6, 1, 1.3] }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeOut',
      }}
    />
  );
}

function ArcSegment({
  size,
  rotation,
  delay,
  duration,
  thickness,
}: {
  size: number;
  rotation: number;
  delay: number;
  duration: number;
  thickness: number;
}) {
  return (
    <motion.div
      className="absolute left-1/2 top-1/2 rounded-full"
      style={{
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        border: `${thickness}px solid transparent`,
        borderTopColor: 'hsl(162 63% 46% / 0.12)',
        borderRightColor: 'hsl(162 63% 46% / 0.06)',
      }}
      initial={{ opacity: 0, rotate: rotation }}
      animate={{ opacity: 1, rotate: rotation + 360 }}
      transition={{
        opacity: { duration: 0.6, delay },
        rotate: { duration, repeat: Infinity, ease: 'linear' },
      }}
    />
  );
}

function GlowOrb({
  x,
  y,
  size,
  color,
  delay,
  duration,
}: {
  x: string;
  y: string;
  size: number;
  color: string;
  delay: number;
  duration: number;
}) {
  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        left: x,
        top: y,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: `blur(${size / 4}px)`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.5, 0], scale: [0.8, 1.2, 0.8] }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

function ConnectorLine({
  x1,
  y1,
  angle,
  length,
  delay,
}: {
  x1: string;
  y1: string;
  angle: number;
  length: number;
  delay: number;
}) {
  return (
    <motion.div
      className="absolute"
      style={{
        left: x1,
        top: y1,
        width: length,
        height: 1,
        transformOrigin: '0 50%',
        transform: `rotate(${angle}deg)`,
        background: 'linear-gradient(90deg, hsl(162 63% 46% / 0.15), transparent)',
      }}
      initial={{ opacity: 0, scaleX: 0 }}
      animate={{ opacity: [0, 0.3, 0], scaleX: [0, 1, 0] }}
      transition={{
        duration: 4,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

export function AuthAnimation() {
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      {/* Background ambient glows */}
      <div className="absolute h-96 w-96 rounded-full bg-primary/[0.04] blur-[120px]" />
      <div className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.03] blur-[100px]" />
      <div className="absolute -left-10 -top-10 h-64 w-64 rounded-full bg-primary/[0.025] blur-[80px]" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(hsl(162 63% 46%) 1px, transparent 1px), linear-gradient(90deg, hsl(162 63% 46%) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />

      {/* Outer rings — large, filling the space */}
      <Ring size={500} duration={50} delay={0.1} opacity={0.03} />
      <Ring size={420} duration={40} delay={0.15} opacity={0.04} reverse />
      <Ring size={340} duration={32} delay={0.2} opacity={0.05} />
      <Ring size={260} duration={26} delay={0.3} opacity={0.07} reverse />
      <Ring size={180} duration={20} delay={0.4} opacity={0.09} />
      <Ring size={110} duration={15} delay={0.5} opacity={0.06} reverse />

      {/* Arc segments — partial ring arcs for visual density */}
      <ArcSegment size={460} rotation={30} delay={0.2} duration={35} thickness={2} />
      <ArcSegment size={380} rotation={120} delay={0.4} duration={28} thickness={1} />
      <ArcSegment size={300} rotation={210} delay={0.3} duration={22} thickness={2} />
      <ArcSegment size={220} rotation={60} delay={0.5} duration={18} thickness={1} />
      <ArcSegment size={150} rotation={300} delay={0.6} duration={14} thickness={2} />

      {/* Pulse rings — expanding waves from center */}
      <PulseRing size={200} delay={0} duration={4} />
      <PulseRing size={200} delay={1.3} duration={4} />
      <PulseRing size={200} delay={2.6} duration={4} />

      {/* Orbiting dots — more orbs across multiple ring layers */}
      <Orb size={6} orbitSize={500} duration={50} delay={0.2} color="hsl(162 63% 46% / 0.4)" startAngle={0} />
      <Orb size={4} orbitSize={500} duration={50} delay={0.2} color="hsl(162 63% 46% / 0.2)" startAngle={120} />
      <Orb size={3} orbitSize={500} duration={50} delay={0.2} color="hsl(162 63% 46% / 0.3)" startAngle={240} />
      <Orb size={5} orbitSize={420} duration={40} delay={0.3} color="hsl(152 60% 48% / 0.5)" startAngle={45} />
      <Orb size={3} orbitSize={420} duration={40} delay={0.3} color="hsl(152 60% 48% / 0.25)" startAngle={200} />
      <Orb size={5} orbitSize={340} duration={32} delay={0.35} color="hsl(162 63% 46% / 0.6)" startAngle={90} />
      <Orb size={3} orbitSize={340} duration={32} delay={0.35} color="hsl(162 63% 46% / 0.3)" startAngle={270} />
      <Orb size={4} orbitSize={260} duration={26} delay={0.4} color="hsl(152 60% 48% / 0.5)" startAngle={160} />
      <Orb size={4} orbitSize={180} duration={20} delay={0.5} color="hsl(162 63% 46% / 0.7)" startAngle={30} />
      <Orb size={3} orbitSize={180} duration={20} delay={0.5} color="hsl(162 63% 46% / 0.4)" startAngle={210} />
      <Orb size={5} orbitSize={110} duration={15} delay={0.6} color="hsl(162 63% 46% / 0.8)" startAngle={0} />

      {/* Glowing orbs — soft pulsing blobs scattered around */}
      <GlowOrb x="8%" y="15%" size={40} color="hsl(162 63% 46% / 0.2)" delay={0} duration={5} />
      <GlowOrb x="82%" y="10%" size={30} color="hsl(152 60% 48% / 0.15)" delay={1.5} duration={6} />
      <GlowOrb x="90%" y="70%" size={50} color="hsl(162 63% 46% / 0.12)" delay={0.8} duration={7} />
      <GlowOrb x="5%" y="80%" size={35} color="hsl(152 60% 48% / 0.18)" delay={2} duration={5.5} />
      <GlowOrb x="70%" y="85%" size={25} color="hsl(162 63% 46% / 0.15)" delay={3} duration={4.5} />
      <GlowOrb x="25%" y="5%" size={30} color="hsl(162 63% 46% / 0.1)" delay={1} duration={6.5} />

      {/* Connector lines — radial lines from center outward */}
      <ConnectorLine x1="50%" y1="50%" angle={25} length={180} delay={0} />
      <ConnectorLine x1="50%" y1="50%" angle={75} length={150} delay={1} />
      <ConnectorLine x1="50%" y1="50%" angle={140} length={200} delay={2} />
      <ConnectorLine x1="50%" y1="50%" angle={200} length={160} delay={0.5} />
      <ConnectorLine x1="50%" y1="50%" angle={280} length={190} delay={1.5} />
      <ConnectorLine x1="50%" y1="50%" angle={330} length={140} delay={2.5} />

      {/* Floating particles — spread across entire area */}
      <FloatingDot x="6%" y="20%" size={3} delay={0} duration={3.5} />
      <FloatingDot x="92%" y="15%" size={2} delay={0.8} duration={4} />
      <FloatingDot x="88%" y="50%" size={3} delay={1.6} duration={3} />
      <FloatingDot x="8%" y="75%" size={3} delay={2.2} duration={3.8} />
      <FloatingDot x="75%" y="88%" size={2} delay={0.5} duration={4.2} />
      <FloatingDot x="35%" y="8%" size={2} delay={1.2} duration={3.2} />
      <FloatingDot x="18%" y="45%" size={2} delay={0.3} duration={3.8} />
      <FloatingDot x="80%" y="35%" size={3} delay={1.8} duration={4.5} />
      <FloatingDot x="55%" y="92%" size={2} delay={2.5} duration={3.6} />
      <FloatingDot x="25%" y="90%" size={2} delay={0.7} duration={4.1} />
      <FloatingDot x="65%" y="5%" size={3} delay={1.4} duration={3.3} />
      <FloatingDot x="95%" y="88%" size={2} delay={2} duration={3.9} />

      {/* Center core */}
      <motion.div
        className="relative z-10 flex h-24 w-24 items-center justify-center rounded-full"
        style={{
          background: 'radial-gradient(circle, hsl(162 63% 46% / 0.12) 0%, transparent 70%)',
        }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.8, ease: 'easeOut' }}
      >
        <motion.div
          className="h-4 w-4 rounded-full bg-primary"
          style={{ boxShadow: '0 0 24px hsl(162 63% 46% / 0.5), 0 0 48px hsl(162 63% 46% / 0.2)' }}
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </div>
  );
}
